import type { Client, FileDetails } from '@/components/Views/Company/File/file.types';
import type { PaginatedParams, PaginatedResponse } from '@/interfaces/pagination.types';
import type { FormattedMoneyString } from '@/shared/utils/money';
import type { Company } from '@/types/closinglock.types';
import type DsDataTable from '@ds/table/DsDataTable.vue';
import type { MaybeRefOrGetter, TemplateRef } from 'vue';

export interface FileTableColumn {
    field: keyof FileTableDisplayRow;
    /**
     * The key sent to the backend as `order_by` when sorting by this column.
     * Usually the same as `field`, but differs for columns whose display field holds a formatted value
     * (e.g. balance_due_formatted) rather than the raw backend column (e.g. balance_due)
     */
    sortField: keyof FileTableRow;
    header: string;
    isSortable: boolean;
    hiddenByDefault: boolean;
}

export type FileRowSlot = { data: FileTableDisplayRow };

export type DatatableElement = Pick<InstanceType<typeof DsDataTable>, 'exportCSV'> | undefined;
export interface FileTableExportOptions {
    dataTable: TemplateRef<DatatableElement>;
    columns: MaybeRefOrGetter<readonly FileTableColumn[]>;
    rows: MaybeRefOrGetter<FileTableDisplayRow[]>;
}

export const FILE_TABLE_COLUMNS = [
    {
        field: 'ref_number',
        sortField: 'ref_number',
        header: 'File reference #',
        isSortable: true,
        hiddenByDefault: false,
    },
    {
        field: 'address',
        sortField: 'address',
        header: 'Property address',
        isSortable: true,
        hiddenByDefault: false,
    },
    {
        field: 'clients_count',
        sortField: 'clients_count',
        header: 'Clients',
        isSortable: false,
        hiddenByDefault: false,
    },
    {
        field: 'balance_due_formatted',
        sortField: 'balance_due',
        header: 'Balance due',
        isSortable: true,
        hiddenByDefault: false,
    },
    {
        field: 'close_date',
        sortField: 'close_date',
        header: 'Close date',
        isSortable: true,
        hiddenByDefault: false,
    },
    {
        field: 'company',
        sortField: 'company',
        header: 'Company',
        isSortable: true,
        hiddenByDefault: false,
    },
    {
        field: 'branch',
        sortField: 'branch',
        header: 'Branch',
        isSortable: true,
        hiddenByDefault: false,
    },
    {
        field: 'vendor',
        sortField: 'vendor',
        header: 'Source',
        isSortable: true,
        hiddenByDefault: true,
    },
    {
        field: 'created_at',
        sortField: 'created_at',
        header: 'Created',
        isSortable: true,
        hiddenByDefault: false,
    },
    {
        field: 'updated_at',
        sortField: 'updated_at',
        header: 'Updated',
        isSortable: true,
        hiddenByDefault: true,
    },
    {
        field: 'owned_by_email',
        sortField: 'owned_by_email',
        header: 'Owned by',
        isSortable: true,
        hiddenByDefault: true,
    },
] as const satisfies readonly FileTableColumn[];

export type FileTableColumnField = (typeof FILE_TABLE_COLUMNS)[number]['field'];

export type FileTableSortField = Extract<(typeof FILE_TABLE_COLUMNS)[number], { isSortable: true }>['sortField'];

export type FileTableRow = Pick<
    FileDetails,
    | 'id'
    | 'ref_number'
    | 'address'
    | 'close_date'
    | 'balance_due'
    | 'branch'
    | 'branch_id'
    | 'vendor'
    | 'created_at'
    | 'updated_at'
    | 'owned_by'
    | 'street_view_url'
    | 'safe_to_delete'
    | 'safe_to_delete_reason'
    | 'notes'
> & {
    company: FileDetails['title'];
    owned_by_email: string;
    clients_count: number;
    clients: Pick<Client, 'id' | 'first' | 'last' | 'email' | 'role'>[];
    routes: Pick<FileDetails['routes'], 'delete_file' | 'edit_file' | 'file_history'> & {
        details: FileDetails['routes']['file_history'];
    };
};

export type FileSearchResponse = PaginatedResponse<FileTableRow> & {
    meta: PaginatedResponse<FileTableRow>['meta'] & {
        total_unfiltered: number;
    };
};

export type FileTableDisplayRow = Omit<FileTableRow, 'balance_due' | 'close_date' | 'company'> & {
    balance_due: FileDetails['balance_due'];
    balance_due_formatted: FormattedMoneyString;
    close_date: FileDetails['close_date'];
    company: Company['name'];
};

export interface FileSearchParams extends PaginatedParams<FileTableSortField> {
    search: string;
}

export type DeletableFile = Pick<FileDetails, 'ref_number' | 'address' | 'safe_to_delete' | 'safe_to_delete_reason'> & {
    routes: Pick<FileDetails['routes'], 'delete_file'>;
};

// a field declared false is gated off. Omissions are treated as allowed fields
export type GatedFileTableColumnOptions = Partial<Record<FileTableColumnField, MaybeRefOrGetter<boolean>>>;

export type FileTableExportEmits = {
    (event: 'exportCsv'): void;
    (event: 'exportClipboard'): void;
};
